import { createContext, useCallback, useContext, useState } from 'react';
import { findFrameUnderPoint, type Point, type SpatialNode } from "@/features/canvas/engine/primitives/geometry/geometry";

export interface UseFrameDropTargetReturn {
  targetFrameId: string | null;
  onDrag: (
    point: Point,
    nodes: readonly SpatialNode[],
    excludeIds: readonly string[],
  ) => void;
  reset: () => void;
}

/**
 * Live drop-target tracker. Wired to React Flow's onNodeDrag in
 * CanvasEngine. The returned id powers the FrameNode highlight (Task 6).
 *
 * Pure logic — geometry comes from `findFrameUnderPoint`. Excluding
 * the dragged node(s) prevents a frame from being its own drop target.
 */
export function useFrameDropTarget(): UseFrameDropTargetReturn {
  const [targetFrameId, setTargetFrameId] = useState<string | null>(null);

  const onDrag = useCallback(
    (point: Point, nodes: readonly SpatialNode[], excludeIds: readonly string[]) => {
      const next = findFrameUnderPoint(point, nodes, excludeIds);
      setTargetFrameId(prev => (prev === next ? prev : next));
    },
    [],
  );

  const reset = useCallback(() => setTargetFrameId(null), []);

  return { targetFrameId, onDrag, reset };
}

// ── Drop-target context ──────────────────────────────────────────────────────

export interface FrameDropTargetContextValue {
  targetFrameId: string | null;
}

/**
 * React context that broadcasts the active drop-target frame id to all
 * FrameNode renders. CanvasEngine wraps the React Flow tree with the
 * provider; FrameNode subscribes via `useActiveFrameDropTarget`.
 *
 * Picking a context (vs mutating Y.Map data on every onNodeDrag tick)
 * keeps the highlight off the Yjs undo stack and avoids churn in the
 * source-of-truth document during a drag.
 */
export const FrameDropTargetContext = createContext<FrameDropTargetContextValue>({
  targetFrameId: null,
});

export function useActiveFrameDropTarget(): string | null {
  return useContext(FrameDropTargetContext).targetFrameId;
}
